import { inject, Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { FirebaseApp } from '@firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  MessagePayload,
  onMessage,
} from '@firebase/messaging';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { getBrowserInfo } from '@fc-shared/utils/browser.utils';
import { BaseNotification } from '@fc-core/services/notification-payload';
import { NotificationsStore } from '@fc-core/notifications/store/notifications.store';
import {
  handlePushPayload,
  isDismissalPayload,
} from '../../../firebase-messaging/message-handler';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private http = inject(HttpClient);
  private notificationsStore = inject(NotificationsStore);
  private app?: FirebaseApp;
  private messaging?: Messaging;

  async init() {
    if (environment.environment === 'local' || !environment.firebaseConfig) {
      return;
    }

    const isFirebaseSupported = await isSupported();
    if (!isFirebaseSupported) {
      console.log("The current browser doesn't support Firebase Messaging.");
      return;
    }

    this.app = initializeApp(environment.firebaseConfig);
    this.messaging = getMessaging(this.app);
    getToken(this.messaging, { vapidKey: environment.vapidKey })
      .then((currentToken) => {
        if (currentToken) {
          const browserInfo = getBrowserInfo();
          this.http
            .post(environment.apiUrl + 'api/push/token', {
              token: currentToken,
              platform: 'web',
              app: 'console',
              metadata: browserInfo,
            })
            .subscribe({
              // Best-effort: init() fetches and re-sends the token on every app load.
              error: () => {},
            });
        } else {
          // Show permission request UI
          console.log(
            'No registration token available. Request permission to generate one.',
          );
          this.requestPermission();
        }
      })
      .catch((err) => {
        console.log('An error occurred while retrieving token. ', err);
      });

    this.listenForForegroundMessages(this.messaging);
  }

  private listenForForegroundMessages(messaging: Messaging) {
    onMessage(messaging, (payload: MessagePayload) =>
      this.handleForegroundMessage(payload as unknown as BaseNotification),
    );
  }

  private async handleForegroundMessage(
    payload: BaseNotification,
  ): Promise<void> {
    const serviceWorker = await navigator.serviceWorker.ready;
    await handlePushPayload(serviceWorker, payload);
    if (isDismissalPayload(payload)) {
      this.refreshLoadedNotificationPanel();
    }
  }

  private refreshLoadedNotificationPanel(): void {
    if (this.notificationsStore.selectors.events().length === 0) return;
    void this.notificationsStore.loadEvents();
  }

  requestPermission() {
    console.log('Requesting permission...');
    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          console.log('Notification permission granted.');
        }
      })
      .catch((err) => {
        console.log('Unable to get permission to notify.', err);
      });
  }
}
